import { useEffect } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';

import { env } from 'config/env';
import { acquireAccessToken, isInteractiveTokenAcquisitionStartedError } from 'services/tokenService';
import type { PaginatedDto } from 'api/billingTypes';
import { notificationKeys, type NotificationListItemDto } from 'hooks/useNotifications';

function buildHubUrl() {
  return `${env.VITE_APP_BUSINESS_SERVICE_BASE_URL.replace(/\/$/, '')}/api/notificationhub`;
}

function upsertNotification(
  current: PaginatedDto<NotificationListItemDto> | undefined,
  notification: NotificationListItemDto
): PaginatedDto<NotificationListItemDto> | undefined {
  if (!current) {
    return current;
  }

  const existingIndex = current.page.findIndex((item) => item.id === notification.id);

  if (existingIndex >= 0) {
    return {
      ...current,
      page: current.page.map((item, index) => (index === existingIndex ? notification : item))
    };
  }

  return {
    ...current,
    allCount: current.allCount + 1,
    page: [notification, ...current.page].slice(0, current.page.length || 20)
  };
}

export function useNotificationHub(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let disposed = false;

    const connection = new HubConnectionBuilder()
      .withUrl(buildHubUrl(), {
        accessTokenFactory: async () => {
          try {
            return await acquireAccessToken();
          } catch (error) {
            if (!isInteractiveTokenAcquisitionStartedError(error)) {
              console.error('Failed to acquire SignalR access token.', error);
            }

            return '';
          }
        }
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('ReceiveNotification', (notification: NotificationListItemDto) => {
      queryClient.setQueriesData<PaginatedDto<NotificationListItemDto>>({ queryKey: notificationKeys.all }, (current) =>
        upsertNotification(current, notification)
      );

      enqueueSnackbar(notification.targy, { variant: 'info' });
    });

    connection.onreconnected(() => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    });

    connection
      .start()
      .then(() => {
        if (disposed && connection.state !== HubConnectionState.Disconnected) {
          return connection.stop();
        }

        return undefined;
      })
      .catch((error) => {
        if (!disposed) {
          console.error('Failed to start notification SignalR connection.', error);
        }
      });

    return () => {
      disposed = true;
      connection.off('ReceiveNotification');

      if (connection.state !== HubConnectionState.Disconnected) {
        void connection.stop();
      }
    };
  }, [enabled, queryClient]);
}
